import { SpriteAssetSpec } from "@westpierstudio/luma/factory/PixiFactory";
import { SplashScreen } from "@westpierstudio/luma/game/SplashScreen";
import { DefaultLayout } from "@westpierstudio/luma/layout";
import { KSGLogo } from "components/KSGLogo";
import { Container, Sprite } from "pixi.js";
import { GameServices } from "./GameServices";

export class LuckyWealthCatSplashScreen extends SplashScreen {
    private spec!: Record<string, any>;
    private backgroundContainer!: Container;
    private background!: Sprite;
    private foregroundContainer!: Container;
    private logo!: KSGLogo;
    private multiContainers!: {landscape: Container, portrait: Container};
    private bonusContainers!: {landscape: Container, portrait: Container};
    private doNotShowContainer!: Container;
    private doNotShowTick!: Sprite;
    private doNotShowBox! : {normal: Sprite, over: Sprite};
    private continueButton!: Container;
    private continueSprites! : {normal: Sprite, over: Sprite};

    constructor(protected override services: GameServices) {
        super(services);
    }

    override setup() {

        if (!this._hasSetup) {

            const {factory, resources} = this.services;

            const spec = resources.getSpec(`splashscreen`);
            this.setupBackground(spec.splashBackground);

            this.foregroundContainer = factory.createContainer(spec.foregroundContainer, this);

            factory.createSprite(spec.splashCat, this.foregroundContainer);
            factory.createSprite(spec.splashLogo, this.foregroundContainer);

            this.logo = this.addChild(new KSGLogo(this.services, this.services.splashscreenLayout));
            this.on(`added`, () => this.logo.resize());

            this.setupMultiContainers(spec.multiContainer);
            this.setupBonusContainers(spec.bonusContainer);
            this.setupDoNotShowContainer(spec.doNotShowContainer);
            this.setupContinueButton(spec.continueButton);

            this.spec = spec;
            this.resize();
        }
    }

    override onContinueClick() {

        this.continueButton.interactive = false;
        this.continueButton.visible = false;
        this.doNotShowContainer.visible = false;

        this.onSplashScreenContinue(this.doNotShowTick.visible);
    }

    override setProgress(_progress: number): void {
    }

    override hideLoadingBar(): void {
    }

    private setupBackground(spec: SpriteAssetSpec):void {

        const {factory, splashscreenLayout} = this.services;

        this.backgroundContainer = factory.createContainer({}, this);
        this.background = factory.createSprite(spec, this.backgroundContainer);

        this.resize();
        splashscreenLayout.onResize.add(() => this.resize());
    }

    private setupMultiContainers(spec: Record<string,any>): void {

        const {factory, resources} = this.services;

        const landscape = factory.createContainer(spec.landscape, this.foregroundContainer);

        factory.createText(resources.getMessage(`splashFeaturing`), spec.landscape.featuringText, landscape);
        factory.createText(resources.getMessage(`splashRepeatingWinLandscape`), spec.landscape.repeatingWinText, landscape);
        factory.createText(resources.getMessage(`splashMultiLandscape`), spec.landscape.multipliersText, landscape);
        factory.createText(resources.getMessage(`splash!`), spec.landscape.exclamationMarkText, landscape);

        const portrait = factory.createContainer(spec.portrait, this.foregroundContainer);

        factory.createText(resources.getMessage(`splashFeaturing`), spec.portrait.featuringText, portrait);
        factory.createText(resources.getMessage(`splashWinMultipliersPortrait`), spec.portrait.repeatingWinMultipliersText, portrait);
        factory.createText(resources.getMessage(`splash!`), spec.portrait.exclamationMarkText, portrait);

        this.multiContainers = {
            landscape,
            portrait
        };
    }

    private setupBonusContainers(spec: Record<string,any>): void {

        const {factory, resources} = this.services;

        const landscape = factory.createContainer(spec.landscape, this.foregroundContainer);

        factory.createSprite(spec.landscape.splashSymbol, landscape);

        factory.createText(resources.getMessage(`splash3`), spec.landscape.threeText, landscape);
        factory.createText(resources.getMessage(`splashInitLandscape`), spec.landscape.initText, landscape);
        factory.createText(resources.getMessage(`splashWheelLandscape`), spec.landscape.wheelText, landscape);
        factory.createText(resources.getMessage(`splash!`), spec.landscape.exclamationMarkText, landscape);

        const portrait = factory.createContainer(spec.portrait, this.foregroundContainer);

        factory.createSprite(spec.portrait.splashSymbol, portrait);

        factory.createText(resources.getMessage(`splash3`), spec.portrait.threeText, portrait);
        factory.createText(resources.getMessage(`splashInitPortrait`), spec.portrait.initText, portrait);
        factory.createText(resources.getMessage(`splashWheelPortrait`), spec.portrait.wheelText, portrait);
        factory.createText(resources.getMessage(`splash!`), spec.portrait.exclamationMarkText, portrait);

        this.bonusContainers = {
            landscape,
            portrait
        };
    }

    private setupDoNotShowContainer(spec: Record<string,any>): void {

        const {factory, resources} = this.services;

        this.doNotShowContainer = factory.createContainer({anchor: 0.5}, this.foregroundContainer);

        this.doNotShowBox = {
            normal: factory.createSprite(spec.splashCheckBox, this.doNotShowContainer),
            over: factory.createSprite(spec.splashCheckBoxOver, this.doNotShowContainer)
        };

        this.doNotShowTick = factory.createSprite(spec.splashTick, this.doNotShowContainer);
        factory.createText(resources.getMessage(`splashDoNotShow`), spec.doNotShowText, this.doNotShowContainer);

        this.doNotShowContainer.interactive = this.doNotShowContainer.buttonMode = true;

        this.doNotShowContainer
        .addListener(`click`, this.onDoNotShowClick, this)
        .addListener(`tap`, this.onDoNotShowClick, this)
        .addListener(`mouseover`, this.showDoNotShowOver, this)
        .addListener(`pointerover`, this.showDoNotShowOver, this)
        .addListener(`mouseout`, this.hideDoNotShowOver, this)
        .addListener(`pointerout`, this.hideDoNotShowOver, this)
        .addListener(`mousedown`, this.hideDoNotShowOver, this)
        .addListener(`pointerdown`, this.hideDoNotShowOver, this);
    }

    private onDoNotShowClick(): void {
        this.doNotShowTick.visible = !this.doNotShowTick.visible;
    }

    private showDoNotShowOver(): void {
        this.doNotShowBox.over.visible = true;
    }

    private hideDoNotShowOver(): void {
        this.doNotShowBox.over.visible = false;
    }

    private setupContinueButton(spec: Record<string,any>): void {

        const {factory, resources} = this.services;

        this.continueButton = factory.createContainer({anchor: 0.5}, this.foregroundContainer);

        this.continueSprites = {
            normal: factory.createSprite(spec.splashContinue, this.continueButton),
            over: factory.createSprite(spec.splashContinueOver, this.continueButton)
        };

        factory.createText(resources.getMessage(`splashContinue`), spec.continueText, this.continueButton);

        this.continueButton.interactive = this.continueButton.buttonMode = true;

        this.continueButton
        .addListener(`click`, this.onContinueClick, this)
        .addListener(`tap`, this.onContinueClick, this)
        .addListener(`mouseover`, this.showContinueOver, this)
        .addListener(`pointerover`, this.showContinueOver, this)
        .addListener(`mouseout`, this.hideContinueOver, this)
        .addListener(`pointerout`, this.hideContinueOver, this)
        .addListener(`mousedown`, this.hideContinueOver, this)
        .addListener(`pointerdown`, this.hideContinueOver, this);
    }

    private showContinueOver(): void {
        this.continueSprites.over.visible = true;
    }

    private hideContinueOver(): void {
        this.continueSprites.over.visible = false;
    }

    private resize(): void {

        const layout = this.services.splashscreenLayout as DefaultLayout;

        const bounds = layout.getScreenBoundaryInBucketSpace();
        const {width, height, left, top} = bounds;

        if (width > height) {
            this.background.width = width;
            this.background.scale.y = this.background.scale.x;
            this.background.x = 0;
            this.background.y = (height - this.background.height) / 2;
        } else {
            this.background.height = height;
            this.background.scale.x = this.background.scale.y;
            this.background.x = (width - this.background.width) / 2;
            this.background.y = 0;
        }

        this.backgroundContainer.x = left;
        this.backgroundContainer.y = top;

        if (this.spec) {

            const {factory} = this.services;

            this.logo.resize();

            if (layout.orientation === `landscape`) {
                factory.applySpec(this.foregroundContainer, this.spec.foregroundContainer.landscape);
                factory.applySpec(this.doNotShowContainer, this.spec.doNotShowContainer.landscape);
                factory.applySpec(this.continueButton, this.spec.continueButton.landscape);

                this.multiContainers.landscape.visible = true;
                this.multiContainers.portrait.visible = false;

                this.bonusContainers.landscape.visible = true;
                this.bonusContainers.portrait.visible = false;
            } else {
                factory.applySpec(this.foregroundContainer, this.spec.foregroundContainer.portrait);
                factory.applySpec(this.doNotShowContainer, this.spec.doNotShowContainer.portrait);
                factory.applySpec(this.continueButton, this.spec.continueButton.portrait);

                this.multiContainers.landscape.visible = false;
                this.multiContainers.portrait.visible = true;

                this.bonusContainers.landscape.visible = false;
                this.bonusContainers.portrait.visible = true;
            }
        }
    }
}